import { useFeatureFlagVariantKey } from 'posthog-js/react';
import React, {
  Suspense, lazy, useEffect, useState,
} from 'react';
import { Router, Route } from 'react-router-dom';
import { canManageVideos } from '~/helpers/video_library_access';
import TeamNotificationsBanner from '~/components/system_banner';
import { checkFeatureFlag, getFeatureFlag } from '~/helpers/FeatureFlagChecker';

const AgentDashboard = lazy(() => import(
  /* webpackChunkName: "agent-dashboard" */ '~/components/pages/agent_dashboard'
));
const PublicReport = lazy(() => import(
  /* webpackChunkName: "public-report" */ '~/components/pages/public_report'
));
const Report = lazy(() => import(
  /* webpackChunkName: "report" */ '~/pages/Report/index'
));
const PhoneNumberSettings = lazy(() => import(
  /* webpackChunkName: "phone-number-settings" */ '~/pages/PhoneNumberSettings/index'
));
const TeamSettings = lazy(() => import(
  /* webpackChunkName: "account" */ '~/pages/AccountSettings/index'
));
const UserSettings = lazy(() => import(
  /* webpackChunkName: "user" */ '~/pages/UserSettings/index'
));
const LandingPageSettings = lazy(() => import(
  /* webpackChunkName: "landing-page" */ '~/pages/LandingPageSettings/index'
));
const TeamMembers = lazy(() => import(
  /* webpackChunkName: "team-members" */ '~/components/pages/account/team_members'
));
const Campaigns = lazy(() => import(
  /* webpackChunkName: "campaigns" */ '~/pages/Campaigns/index'
));
const EditCampaign = lazy(() => import(
  /* webpackChunkName: "campaigns" */ '~/pages/Campaigns/components/EditCampaign'
));
const EditCampaignLibrary = lazy(() => import(
  /* webpackChunkName: "campaign-library" */ '~/pages/CampaignsLibrary/edit'
));
const OnboardApp = lazy(() => import(
  /* webpackChunkName: "onboard-app" */ '~/components/onboard_app'
));
const Labels = lazy(() => import(
  /* webpackChunkName: "labels" */ '~/pages/Labels/index'
));
const CampaignTriggers = lazy(() => import(
  /* webpackChunkName: "campaign-triggers" */ '~/pages/CampaignTriggers/index'
));
const NewOrEditCampaignTrigger = lazy(() => import(
  /* webpackChunkName: "campaign-triggers" */ '~/pages/CampaignTriggers/NewOrEdit'
));
const CampaignsLibrary = lazy(() => import(
  /* webpackChunkName: "campaign-library" */ '~/pages/CampaignsLibrary/index'
));
const PreviewCampaignsLibrary = lazy(() => import(
  /* webpackChunkName: "campaign-library" */ '~/pages/CampaignsLibrary/preview'
));
const ManageData = lazy(() => import(
  /* webpackChunkName: "data" */ '~/pages/Data/index'
));
const SignUp = lazy(() => import(
  /* webpackChunkName: "sign-up" */ '~/components/pages/users/sign_up'
));
const Register = lazy(() => import(
  /* webpackChunkName: "sign-up" */ '~/components/pages/users/register'
));
const NewOrEditSearchQuery = lazy(() => import(
  /* webpackChunkName: "search-queries" */ '~/pages/Data/components/MlsImport/Form'
));
const ManageMlsConfiguration = lazy(() => import(
  /* webpackChunkName: "mls" */ '~/pages/MlsConfiguration'
));
const VideoPreview = lazy(() => import(
  /* webpackChunkName: "videos" */ '~/components/pages/videos/preview'
));
const Inbox = lazy(() => import(
  /* webpackChunkName: "inbox" */ '~/pages/Inbox/index'
));
const DocumentPreview = lazy(() => import(
  /* webpackChunkName: "documents" */ '~/components/pages/documents/preview'
));
const NewOrEditJob = lazy(() => import(
  /* webpackChunkName: "jobs" */ '~/components/pages/jobs/NewOrEditJob'
));
const JobApplication = lazy(() => import(
  /* webpackChunkName: "jobs" */ '~/components/pages/jobs/JobApplication'
));

// TODO: [Teefan] AppRouter could be improved?
// https://reacttraining.com/react-router/web/example/route-config
const AppRouter = () => {
  const [isDmsEnabled, setIsDmsEnabled] = useState(false);
  const [isVideoManagementEnabled, setIsVideoManagementEnabled] = useState(false);
  const jobsFeatureVariant = useFeatureFlagVariantKey(
    process.env.SHAKAPACKER_PUBLIC_ENABLE_JOBS_ENTITY_FLAG,
  );
  const isJobsFeatureEnabled = jobsFeatureVariant === 'displayed';
  const isJobsUpgradeCta = jobsFeatureVariant === 'upgrade_cta';

  useEffect(() => {
    // Apply TinyMCE focus fix globally
    const handleFocusIn = (e) => {
      if ($(e.target).closest('.tox-tinymce, .tox-tinymce-aux, .moxman-window, .tam-assetmanager-root').length) {
        e.stopImmediatePropagation();
      }
    };

    $(document).on('focusin', handleFocusIn);

    // Cleanup function to remove the event listener if needed
    return () => {
      $(document).off('focusin', handleFocusIn);
    };
  }, []);

  useEffect(() => {
    const handlePosthogReady = () => {
      const videoManagementVariant = getFeatureFlag(process.env.SHAKAPACKER_PUBLIC_ENABLE_VIDEO_MANAGEMENT_FLAG);
      const dmsEnabled = checkFeatureFlag(process.env.SHAKAPACKER_PUBLIC_ENABLE_DOCUMENT_MANAGEMENT_FLAG);

      const showVideoManagement = ['displayed', 'upgrade_cta'].includes(videoManagementVariant);

      setIsDmsEnabled(dmsEnabled);
      setIsVideoManagementEnabled(showVideoManagement);
    };

    const checkFeatureFlags = () => {
      if (window.isPosthogInitialized) {
        handlePosthogReady();
      } else {
        window.addEventListener('posthogReady', handlePosthogReady);
      }
    };

    // Listen for route changes
    const unsubscribeHistory = browserHistory.listen(() => {
      const path = window.location.pathname;
      if (path.includes('/videos') || path.includes('/jobs') || path.includes('/documents')) {
        checkFeatureFlags();
      }
    });

    // Check on initial load for all feature-flagged routes
    checkFeatureFlags();

    return () => {
      window.removeEventListener('posthogReady', handlePosthogReady);
      unsubscribeHistory();
    };
  }, []);

  const renderVideoManagement = (routeProps) => {
    if (!canManageVideos()) {
      return <div role="alert">You do not have permission to manage videos.</div>;
    }
    return isVideoManagementEnabled ? <AgentDashboard {...routeProps} /> : null;
  };

  return (
    <Router history={browserHistory}>
      <Suspense fallback={<div>Loading...</div>}>
        <div className="router-root">
          {/*
            System-wide banners (PRD R1 AC 6.2 — Zapier disconnection alerts,
            and any future kind from team_notifications). Admin-only; gated
            here so non-admins never make the wasted round-trip. The API
            endpoint also returns an empty result for non-admins as a
            second line of defense.
          */}
          {Rails.abilities?.manageCurrentTeam && <TeamNotificationsBanner />}
          {/* Recruiting routes */}
          <Route exact path="/recruiter/today" component={AgentDashboard} />
          <Route exact path="/recruiter/leads" component={AgentDashboard} />
          <Route exact path="/recruiter/backlog" component={AgentDashboard} />
          <Route exact path="/recruiter/tasks" component={AgentDashboard} />
          <Route exact path="/recruiter/tasks_due_today" component={AgentDashboard} />
          <Route exact path="/recruiter/tasks_due_later" component={AgentDashboard} />
          <Route exact path="/recruiter/tasks_overdue" component={AgentDashboard} />
          {(isDmsEnabled) && (
            <>
              <Route exact path="/recruiter/documents" component={AgentDashboard} />
              <Route path="/recruiter/documents/trash" component={AgentDashboard} />
            </>
          )}
          <Route exact path="/recruiter/videos" render={renderVideoManagement} />
          <Route path="/recruiter/videos/trash" render={renderVideoManagement} />
          <Route exact path="/recruiter/search" component={AgentDashboard} />
          <Route exact path="/recruiter/saved_searches" component={AgentDashboard} />
          <Route exact path="/recruiter/reports" component={Report} />
          <Route exact path="/recruiter/inbox" component={Inbox} />

          <Route exact path="/recruiter/campaigns" component={Campaigns} />
          <Route exact path="/recruiter/campaigns/new" component={EditCampaign} />
          <Route exact path="/recruiter/campaigns/:id/edit" component={EditCampaign} />
          <Route exact path="/recruiter/campaign_libraries" component={CampaignsLibrary} />
          <Route exact path="/recruiter/campaign_libraries/:id" component={PreviewCampaignsLibrary} />
          <Route exact path="/recruiter/campaign_libraries/:id/edit" component={EditCampaignLibrary} />
          <Route exact path="/recruiter/campaign_triggers" component={CampaignTriggers} />
          <Route exact path="/recruiter/campaign_triggers/new" component={NewOrEditCampaignTrigger} />
          <Route exact path="/recruiter/campaign_triggers/:id/edit" component={NewOrEditCampaignTrigger} />
          {(isJobsUpgradeCta || isJobsFeatureEnabled) && (
            <Route exact path="/recruiter/jobs" component={AgentDashboard} />
          )}
          {isJobsFeatureEnabled && (
            <>
              {Rails.abilities.manageJob && (
                <>
                  <Route exact path="/recruiter/jobs/new" component={NewOrEditJob} />
                  <Route exact path="/recruiter/jobs/:id/edit" component={NewOrEditJob} />
                </>
              )}
              <Route exact path="/recruiter/jobs/:id/candidates" component={AgentDashboard} />
            </>
          )}
          <Route exact path="/recruiter/team/landing_page/edit" component={LandingPageSettings} />
          <Route exact path="/recruiter/labels" component={Labels} />
          <Route exact path="/recruiter/data" component={ManageData} />
          <Route exact path="/recruiter/search_queries/new" component={NewOrEditSearchQuery} />
          <Route exact path="/recruiter/search_queries/:id/edit" component={NewOrEditSearchQuery} />
          <Route exact path="/recruiter/configure_mls/:id" component={ManageMlsConfiguration} />

          {/* Retention routes */}
          <Route exact path="/retention/today" component={AgentDashboard} />
          <Route exact path="/retention/team" component={AgentDashboard} />
          <Route exact path="/retention/tasks" component={AgentDashboard} />
          <Route exact path="/retention/tasks_due_today" component={AgentDashboard} />
          <Route exact path="/retention/tasks_due_later" component={AgentDashboard} />
          <Route exact path="/retention/tasks_overdue" component={AgentDashboard} />
          {(isDmsEnabled) && (
            <>
              <Route exact path="/retention/documents" component={AgentDashboard} />
              <Route path="/retention/documents/trash" component={AgentDashboard} />
            </>
          )}
          <Route exact path="/retention/videos" render={renderVideoManagement} />
          <Route path="/retention/videos/trash" render={renderVideoManagement} />
          <Route exact path="/retention/backlog" component={AgentDashboard} />
          <Route exact path="/retention/search" component={AgentDashboard} />
          <Route exact path="/retention/saved_searches" component={AgentDashboard} />
          <Route exact path="/retention/reports" component={Report} />

          <Route exact path="/retention/campaigns" component={Campaigns} />
          <Route exact path="/retention/campaigns/new" component={EditCampaign} />
          <Route exact path="/retention/campaigns/:id/edit" component={EditCampaign} />
          <Route exact path="/retention/campaign_libraries" component={CampaignsLibrary} />
          <Route exact path="/retention/campaign_libraries/:id" component={PreviewCampaignsLibrary} />
          <Route exact path="/retention/campaign_libraries/:id/edit" component={EditCampaignLibrary} />
          <Route exact path="/retention/campaign_triggers" component={CampaignTriggers} />
          <Route exact path="/retention/campaign_triggers/new" component={NewOrEditCampaignTrigger} />
          <Route exact path="/retention/campaign_triggers/:id/edit" component={NewOrEditCampaignTrigger} />
          <Route exact path="/retention/labels" component={Labels} />
          <Route exact path="/retention/data" component={ManageData} />
          <Route exact path="/retention/search_queries/new" component={NewOrEditSearchQuery} />
          <Route exact path="/retention/search_queries/:id/edit" component={NewOrEditSearchQuery} />
          <Route exact path="/retention/configure_mls/:id" component={ManageMlsConfiguration} />
          <Route exact path="/retention/inbox" component={Inbox} />

          {/* Other routes */}
          <Route exact path="/team/landing_page/edit" render={() => <LandingPageSettings variant="account" />} />

          <Route exact path="/agent/dashboard" component={AgentDashboard} />
          <Route path="/archived/search" component={AgentDashboard} />
          <Route exact path="/archived/saved_searches" component={AgentDashboard} />
          <Route exact path="/archived" component={AgentDashboard} />

          <Route exact path="/r/:code" component={PublicReport} />
          <Route exact path="/onboard/referrers" component={OnboardApp} />

          <Route exact path="/users/edit" component={UserSettings} />
          <Route exact path="/users/phone_number/edit" component={PhoneNumberSettings} />
          <Route exact path="/team/edit" component={TeamSettings} />
          <Route exact path="/team/members" component={TeamMembers} />

          <Route exact path="/campaigns" component={Campaigns} />
          <Route exact path="/campaigns/new" component={EditCampaign} />
          <Route exact path="/campaigns/:id/edit" component={EditCampaign} />

          <Route exact path="/campaign_libraries" component={CampaignsLibrary} />
          <Route exact path="/campaign_libraries/:id" component={PreviewCampaignsLibrary} />
          <Route exact path="/campaign_libraries/:id/edit" component={EditCampaignLibrary} />

          <Route exact path="/labels" component={Labels} />

          <Route exact path="/campaign_triggers" component={CampaignTriggers} />
          <Route exact path="/campaign_triggers/new" component={NewOrEditCampaignTrigger} />
          <Route exact path="/campaign_triggers/:id/edit" component={NewOrEditCampaignTrigger} />

          <Route exact path="/data" component={ManageData} />

          <Route exact path="/signup" component={SignUp} />
          <Route exact path="/register" component={Register} />

          <Route exact path="/search_queries/new" component={NewOrEditSearchQuery} />
          <Route exact path="/search_queries/:id/edit" component={NewOrEditSearchQuery} />

          <Route exact path="/configure_mls/:id" component={ManageMlsConfiguration} />
          <Route exact path="/video/:id" component={VideoPreview} />
          {isDmsEnabled && (
            <Route exact path="/documents/:id" component={DocumentPreview} />
          )}
          <Route exact path="/d/:id" component={DocumentPreview} />
          <Route exact path="/jobs/:id" component={JobApplication} />
        </div>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
